























































































import {
  defineComponent,
  nextTick,
  onMounted,
  provide,
  Ref,
  ref,
  watch,
} from '@nuxtjs/composition-api';
import {
  SfAccordion,
  SfButton,
  SfFilter,
  SfHeading,
  SfRadio,
  SfSidebar,
} from '@storefront-ui/vue';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { encode } from 'js-base64';

import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import { useUiHelpers } from '~/composables';
import { getProductFilterByCategoryCommand } from '~/modules/catalog/category/components/filters/command/getProductFilterByCategoryCommand';
import SelectedFilters from '~/modules/catalog/category/components/filters/FiltersSidebar/SelectedFilters.vue';
import {
  getFilterConfig,
  isFilterEnabled,
} from '~/modules/catalog/category/config/FiltersConfig';
import type { Aggregation, AggregationOption } from '~/modules/GraphQL/types';

import type { SelectedFiltersInterface } from './useFilters';
import { useFilters } from './useFilters';

export interface UseFiltersProviderInterface {
  selectedFilters: Ref<SelectedFiltersInterface>;
  filters: Ref<Aggregation[]>;
}

export default defineComponent({
  name: 'CategoryFilters',
  components: {
    SelectedFilters,
    SkeletonLoader,
    CheckboxType: () =>
      import(
        '~/modules/catalog/category/components/filters/renderer/CheckboxType.vue'
      ),
    SwatchColorType: () =>
      import(
        '~/modules/catalog/category/components/filters/renderer/SwatchColorType.vue'
      ),
    RadioType: () =>
      import(
        '~/modules/catalog/category/components/filters/renderer/RadioType.vue'
      ),
    YesNoType: () =>
      import(
        '~/modules/catalog/category/components/filters/renderer/YesNoType.vue'
      ),
    SfSidebar,
    SfHeading,
    SfAccordion,
    SfFilter,
    SfButton,
    SfRadio,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    catUid: {
      type: String,
      required: true,
    },
    aggregations: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  setup(props, { emit }) {
    const { changeFilters, clearFilters } = useUiHelpers();
    const removableFilters = ref([]);
    const filters = ref<Aggregation[]>([]);
    const isLoading = ref(true);

    const {
      selectedFilters,
      selectFilter,
      removeFilter,
      isFilterSelected,
      getRemovableFilters,
    } = useFilters();

    const updateRemovableFilters = () => {
      removableFilters.value = getRemovableFilters(
        filters.value,
        selectedFilters.value
      );
    };

    const doApplyFilters = () => {
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      if (window?.scroll) {
        window.scroll(0, 0);
      }
      emit('reloadProducts');
      emit('close');
    };

    const doRemoveFilter = ({ id, value }: { id: string; value: string }) => {
      removeFilter(id, value);
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      emit('reloadProducts');
      emit('close');
    };

    const doClearFilters = () => {
      clearFilters(false);
      selectedFilters.value = {};
      updateRemovableFilters();
      emit('reloadProducts');
      emit('close');
    };

    watch(
      () => props.isVisible,
      (newValue) => {
        // disable Storefrontt UI's body scroll lock which is launched when :visible prop on SfSidebar changes
        // two next ticks because SfSidebar uses nextTick aswell, and we want to do something after that tick.
        if (newValue) {
          nextTick(() => nextTick(() => clearAllBodyScrollLocks()));
        }
      }
    );

    onMounted(async () => {
      const loadedFilters =
        props.aggregations.length > 0
          ? (props.aggregations as Aggregation[])
          : await getProductFilterByCategoryCommand.execute({
            eq: props.catUid,
          });
      // add ：Solve the problem of M2 filtering with attribute_code==category_uid  option value being category_id.
      loadedFilters.forEach(
        (item: Aggregation, index, filter: Aggregation[]) => {
          if (item.attribute_code === 'category_uid') {
            const newOptions: AggregationOption[] = item.options?.map(
              (option, k) => {
                option.value = encode(option.value);
                return option;
              }
            );
            filter[index].options = newOptions;
          }
          if (item.attribute_code === 'brands') {
            const newBrands: AggregationOption[] = item.options
              ?.slice()
              ?.sort((a, b) => {
                if (a.label < b.label) {
                  return -1;
                }
                if (a.label > b.label) {
                  return 1;
                }
                return 0;
              });
            filter[index].options = newBrands;
          }
        }
      );

      filters.value = loadedFilters.filter((filter: Aggregation) =>
        isFilterEnabled(filter.attribute_code)
      );
      console.log('filters', filters);
      updateRemovableFilters();
      isLoading.value = false;
    });

    provide('UseFiltersProvider', {
      isFilterSelected,
      selectedFilters,
      filters,
    });

    return {
      selectFilter,
      doApplyFilters,
      doRemoveFilter,
      doClearFilters,
      getFilterConfig,
      selectedFilters,
      filters,
      isLoading,
      removableFilters,
    };
  },
});
